import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  CheckCircle, 
  Circle, 
  Upload, 
  Info, 
  Palette, 
  Globe, 
  X,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Star,
  Users,
  Zap,
  Building,
  Mail,
  Phone,
  Save,
  Check,
  AlertCircle
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'
import { Button } from '../ui/button'
import { cn } from '../../utils/cn'
import { useBranding } from '../../context/BrandingContext'

const OnboardingModal = ({ isOpen, onClose, onComplete }) => {
  const { cmsName } = useBranding()
  const [currentStep, setCurrentStep] = useState(0)
  const [isStepByStep, setIsStepByStep] = useState(false)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Form states for each step
  const [formData, setFormData] = useState({
    // Profile data
    company_name: '',
    email: '',
    phone: '',
    address: '',
    description: '',
    domain: '',
    // Brand data
    logo: null,
    brand_colors: ['#3B82F6', '#1E40AF'] // Default blue colors
  })
  
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [errors, setErrors] = useState({})
  const [stepCompletions, setStepCompletions] = useState({})

  // Fetch onboarding checklist status
  const { data: checklist, isLoading, refetch } = useQuery(
    'onboarding-checklist',
    () => apiService.get('/clients/onboarding/checklist').then(res => res.data),
    {
      refetchOnWindowFocus: false,
      enabled: isOpen,
      refetchInterval: isOpen ? 30000 : false, // Poll every 30 seconds when modal is open
    }
  )

  // Fetch client profile to populate form
  const { data: profile } = useQuery(
    'client-profile',
    () => apiService.get('/clients/profile').then(res => res.data),
    {
      enabled: isOpen,
      onSuccess: (data) => {
        setFormData(prev => ({
          ...prev,
          company_name: data.company_name || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          description: data.description || '',
          domain: data.domain || '',
          brand_colors: data.brand_colors || prev.brand_colors
        }))
      }
    }
  )

  // Update profile mutation
  const updateProfileMutation = useMutation(
    async (profileData) => {
      const response = await apiService.put('/clients/profile', profileData)
      
      // Mark about section as completed
      await apiService.post('/clients/onboarding/update', {
        itemId: 'about',
        completed: true
      })
      
      // Mark domain as connected if domain was provided
      if (profileData.domain && profileData.domain.trim()) {
        await apiService.post('/clients/onboarding/update', {
          itemId: 'domain',
          completed: true
        })
      }
      
      return response
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('client-profile')
        queryClient.invalidateQueries('onboarding-checklist')
        setStepCompletions(prev => ({ ...prev, about: true, domain: formData.domain ? true : false }))
      },
      onError: (error) => {
        setErrors(prev => ({ ...prev, profile: error.response?.data?.error || 'Failed to save profile' }))
      }
    }
  )

  // Upload logo mutation
  const uploadLogoMutation = useMutation(
    async (logoFile) => {
      const formDataUpload = new FormData()
      formDataUpload.append('logo', logoFile)
      
      // Upload logo to Azure Storage
      const response = await apiService.post('/clients/logo', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      
      // Mark logo as uploaded
      await apiService.post('/clients/onboarding/update', {
        itemId: 'logo',
        completed: true
      })
      
      return response
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('onboarding-checklist')
        setStepCompletions(prev => ({ ...prev, logo: true }))
        setErrors(prev => ({ ...prev, logo: null }))
      },
      onError: (error) => {
        setErrors(prev => ({ ...prev, logo: error.response?.data?.error || 'Failed to upload logo' }))
      }
    }
  )

  // Update brand colors mutation
  const updateBrandColorsMutation = useMutation(
    async (colors) => {
      const response = await apiService.put('/clients/profile', { brand_colors: colors })
      
      // Mark brand color as set
      await apiService.post('/clients/onboarding/update', {
        itemId: 'brand_color',
        completed: true
      })
      
      return response
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('client-profile')
        queryClient.invalidateQueries('onboarding-checklist')
        setStepCompletions(prev => ({ ...prev, brand_color: true }))
        setErrors(prev => ({ ...prev, brand_color: null }))
      },
      onError: (error) => {
        setErrors(prev => ({ ...prev, brand_color: error.response?.data?.error || 'Failed to save brand colors' }))
      }
    }
  )

  // Complete onboarding mutation
  const completeOnboardingMutation = useMutation(
    () => apiService.post('/clients/onboarding/complete'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('onboarding-checklist')
        queryClient.invalidateQueries('client-profile')
        if (onComplete) onComplete()
        if (onClose) onClose()
      }
    }
  )

  // Skip onboarding for now (still shows next time)
  const handleSkipForNow = () => {
    if (onClose) onClose()
  }

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleLogoUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      setFormData(prev => ({ ...prev, logo: file }))
      uploadLogoMutation.mutate(file)
    }
  }

  const handleColorChange = (index, color) => {
    const newColors = [...formData.brand_colors]
    newColors[index] = color
    setFormData(prev => ({ ...prev, brand_colors: newColors }))
  }

  const handleSaveProfile = () => {
    const profileData = {
      company_name: formData.company_name,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      description: formData.description,
      domain: formData.domain
    }
    updateProfileMutation.mutate(profileData)
  }

  const handleSaveBrandColors = () => {
    updateBrandColorsMutation.mutate(formData.brand_colors)
  }

  // Get step-by-step flow data with inline forms
  const getStepByStepFlow = () => {
    if (!checklist) return []
    
    return [
      {
        id: 'welcome',
        title: `Welcome to ${cmsName}!`,
        description: 'Let\'s get you set up in just a few steps',
        icon: Sparkles,
        color: 'primary',
        isAction: false,
        content: (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mx-auto mb-6">
              <Star className="w-10 h-10 text-primary-600 dark:text-primary-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Welcome to {cmsName}!
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              We're excited to help you create amazing websites. Let's get your account set up with these quick steps.
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4" />
                <span>Trusted by 1000+ clients</span>
              </div>
              <div className="flex items-center space-x-2">
                <Zap className="w-4 h-4" />
                <span>Setup in 5 minutes</span>
              </div>
            </div>
          </div>
        )
      },
      {
        id: 'logo',
        title: 'Upload Your Logo',
        description: 'Add your company logo to personalize your websites',
        icon: Upload,
        color: 'blue',
        isAction: true,
        completed: checklist.checklist.find(item => item.id === 'logo')?.completed || stepCompletions.logo,
        required: true,
        content: (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-6">
                <Upload className="w-10 h-10 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Upload Your Logo
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
                Add your company logo to personalize your websites and create a professional brand presence.
              </p>
            </div>

            <div className="max-w-md mx-auto">
              <div className="border-2 border-dashed border-gray-300 dark:border-neutral-700 rounded-lg p-8 text-center hover:border-primary-500 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <label htmlFor="logo-upload" className="cursor-pointer">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    PNG, JPG, GIF up to 10MB
                  </p>
                </label>
              </div>

              {formData.logo && (
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    <span className="text-sm text-green-700 dark:text-green-200">
                      {formData.logo.name} selected
                    </span>
                  </div>
                </div>
              )}

              {uploadLogoMutation.isLoading && (
                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                    <span className="text-sm text-blue-700 dark:text-blue-200">
                      Uploading logo...
                    </span>
                  </div>
                </div>
              )}

              {errors.logo && (
                <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                    <span className="text-sm text-red-700 dark:text-red-200">
                      {errors.logo}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      },
      {
        id: 'about',
        title: 'Complete Your Profile',
        description: 'Fill in your company information and details',
        icon: Info,
        color: 'green',
        isAction: true,
        completed: checklist.checklist.find(item => item.id === 'about')?.completed || stepCompletions.about,
        required: true,
        content: (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building className="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Complete Your Profile
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
                Tell us about your company so we can personalize your experience.
              </p>
            </div>

            <div className="max-w-md mx-auto space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-dark-200 text-gray-900 dark:text-white"
                  placeholder="Enter your company name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-dark-200 text-gray-900 dark:text-white"
                    placeholder="company@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-dark-200 text-gray-900 dark:text-white"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Company Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-dark-200 text-gray-900 dark:text-white"
                  placeholder="Tell us about your company..."
                />
              </div>

              <Button
                onClick={handleSaveProfile}
                disabled={updateProfileMutation.isLoading || !formData.company_name.trim()}
                className="w-full"
              >
                <Save className="w-4 h-4 mr-2" />
                {updateProfileMutation.isLoading ? 'Saving...' : 'Save Profile'}
              </Button>

              {updateProfileMutation.isSuccess && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    <span className="text-sm text-green-700 dark:text-green-200">
                      Profile saved successfully!
                    </span>
                  </div>
                </div>
              )}

              {errors.profile && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                    <span className="text-sm text-red-700 dark:text-red-200">
                      {errors.profile}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      },
      {
        id: 'brand_color',
        title: 'Set Brand Colors',
        description: 'Choose colors that represent your brand',
        icon: Palette,
        color: 'purple',
        isAction: true,
        completed: checklist.checklist.find(item => item.id === 'brand_color')?.completed || stepCompletions.brand_color,
        required: true,
        content: (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center mx-auto mb-6">
                <Palette className="w-10 h-10 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Set Brand Colors
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
                Choose colors that represent your brand. These will be used throughout your websites.
              </p>
            </div>

            <div className="max-w-md mx-auto space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Primary Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={formData.brand_colors[0]}
                      onChange={(e) => handleColorChange(0, e.target.value)}
                      className="w-12 h-12 rounded-lg border border-gray-300 dark:border-neutral-700 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.brand_colors[0]}
                      onChange={(e) => handleColorChange(0, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-dark-200 text-gray-900 dark:text-white"
                      placeholder="#3B82F6"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Secondary Color
                  </label>
                  <div className="flex items-center space-x-3">
                    <input
                      type="color"
                      value={formData.brand_colors[1]}
                      onChange={(e) => handleColorChange(1, e.target.value)}
                      className="w-12 h-12 rounded-lg border border-gray-300 dark:border-neutral-700 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.brand_colors[1]}
                      onChange={(e) => handleColorChange(1, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-dark-200 text-gray-900 dark:text-white"
                      placeholder="#1E40AF"
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 bg-neutral-50 dark:bg-dark-200 rounded-lg">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Preview</h4>
                <div className="flex space-x-2">
                  <div 
                    className="w-16 h-16 rounded-lg shadow-sm"
                    style={{ backgroundColor: formData.brand_colors[0] }}
                  />
                  <div 
                    className="w-16 h-16 rounded-lg shadow-sm"
                    style={{ backgroundColor: formData.brand_colors[1] }}
                  />
                </div>
              </div>

              <Button
                onClick={handleSaveBrandColors}
                disabled={updateBrandColorsMutation.isLoading}
                className="w-full"
              >
                <Palette className="w-4 h-4 mr-2" />
                {updateBrandColorsMutation.isLoading ? 'Saving...' : 'Save Brand Colors'}
              </Button>

              {updateBrandColorsMutation.isSuccess && (
                <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex items-center">
                    <Check className="w-4 h-4 text-green-500 mr-2" />
                    <span className="text-sm text-green-700 dark:text-green-200">
                      Brand colors saved successfully!
                    </span>
                  </div>
                </div>
              )}

              {errors.brand_color && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <div className="flex items-center">
                    <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
                    <span className="text-sm text-red-700 dark:text-red-200">
                      {errors.brand_color}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      },
      {
        id: 'domain',
        title: 'Your Website URL',
        description: 'Your site is already live on a subdomain — upgrade to a custom domain anytime',
        icon: Globe,
        color: 'yellow',
        isAction: true,
        completed: checklist.checklist.find(item => item.id === 'domain')?.completed || stepCompletions.domain,
        required: false,
        content: (
          <div className="space-y-6">
            <div className="text-center">
              <div className="w-20 h-20 bg-yellow-100 dark:bg-yellow-900 rounded-full flex items-center justify-center mx-auto mb-6">
                <Globe className="w-10 h-10 text-yellow-600 dark:text-yellow-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Your Website URL
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
                Your site is already accessible on a subdomain. You can connect your own domain anytime from the Domain settings.
              </p>
            </div>

            <div className="max-w-md mx-auto space-y-4">
              {/* Assigned subdomain display */}
              {profile?.assigned_subdomain && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                  <p className="text-xs font-medium text-green-700 dark:text-green-300 mb-1">Your subdomain (ready now)</p>
                  <div className="flex items-center justify-between gap-2">
                    <code className="text-sm font-mono text-green-800 dark:text-green-200 break-all">
                      https://{profile.assigned_subdomain}
                    </code>
                    <a
                      href={`https://${profile.assigned_subdomain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-green-600 hover:text-green-800 dark:text-green-400"
                    >
                      <Globe className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              )}

              <div className="border-t border-gray-200 dark:border-neutral-700 pt-4">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Have your own domain? (optional)
                </p>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="url"
                    name="domain"
                    value={formData.domain}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-neutral-700 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-dark-200 text-gray-900 dark:text-white"
                    placeholder="https://yourwebsite.com"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Full setup instructions will be in the Domain settings page.
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleSaveProfile}
                  disabled={updateProfileMutation.isLoading || !formData.domain}
                  className="flex-1"
                >
                  <Globe className="w-4 h-4 mr-2" />
                  {updateProfileMutation.isLoading ? 'Saving...' : 'Save Custom Domain'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(currentStep + 1)}
                >
                  Skip
                </Button>
              </div>
            </div>
          </div>
        )
      },
      {
        id: 'complete',
        title: 'You\'re All Set!',
        description: 'Your account is ready to create amazing websites',
        icon: CheckCircle,
        color: 'green',
        isAction: false,
        content: (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              Congratulations! 🎉
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              Your account is now set up and ready to go. You can start creating your first website or explore the dashboard.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => completeOnboardingMutation.mutate()}
                disabled={completeOnboardingMutation.isLoading}
                className="w-full"
              >
                {completeOnboardingMutation.isLoading ? 'Completing...' : 'Complete Setup'}
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/client/sites/new')}
                className="w-full"
              >
                Create Your First Website
              </Button>
            </div>
          </div>
        )
      }
    ]
  }

  const steps = getStepByStepFlow()
  const currentStepData = steps[currentStep]
  const completedRequiredSteps = steps.filter(step => 
    step.isAction && step.required && step.completed
  ).length
  const totalRequiredSteps = steps.filter(step => step.isAction && step.required).length

  // Auto-advance to completion step when all required steps are done
  useEffect(() => {
    if (checklist && completedRequiredSteps === totalRequiredSteps && currentStep < steps.length - 1) {
      setCurrentStep(steps.length - 1) // Go to completion step
    }
  }, [completedRequiredSteps, totalRequiredSteps, currentStep, steps.length, checklist])

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  // Remove the redirect action handler since we now have inline forms

  if (!isOpen || isLoading || !checklist) {
    return null
  }

  // Don't show if already completed
  if (checklist.onboardingCompleted) {
    return null
  }

  const WelcomeView = () => (
    <div className="p-8">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mx-auto mb-4">
          <Star className="w-8 h-8 text-primary-600 dark:text-primary-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Welcome to WebNest!
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Let's get your account set up in just a few steps
        </p>
      </div>

      {/* Progress Overview */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Setup Progress
          </span>
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {checklist.completedCount}/{checklist.totalCount}
          </span>
        </div>
        <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-2">
          <div 
            className="bg-accent-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(checklist.completedCount / checklist.totalCount) * 100}%` }}
          />
        </div>
      </div>

      {/* Checklist Items */}
      <div className="space-y-4 mb-8">
        {checklist.checklist.map((item) => (
          <div key={item.id} className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-dark-200 rounded-lg">
            <div className="flex items-center space-x-3">
              {item.completed ? (
                <CheckCircle className="w-5 h-5 text-green-500" />
              ) : (
                <Circle className="w-5 h-5 text-gray-400" />
              )}
              <div>
                <h4 className={cn(
                  "text-sm font-medium",
                  item.completed 
                    ? "text-gray-500 dark:text-gray-400 line-through" 
                    : "text-gray-900 dark:text-white"
                )}>
                  {item.title}
                  {item.required && !item.completed && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {item.description}
                </p>
              </div>
            </div>
            {!item.completed && (
              <button
                onClick={() => {
                  setIsStepByStep(true)
                  // Find the step index for this item
                  const stepIndex = steps.findIndex(step => step.id === item.id)
                  if (stepIndex > 0) {
                    setCurrentStep(stepIndex)
                  }
                }}
                className="text-primary-600 hover:text-primary-500 text-sm font-medium"
              >
                Start
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="space-y-3">
        <Button
          onClick={() => setIsStepByStep(true)}
          className="w-full"
        >
          Start Step-by-Step Setup
        </Button>

        {checklist.isComplete && (
          <Button
            onClick={() => completeOnboardingMutation.mutate()}
            disabled={completeOnboardingMutation.isLoading}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            {completeOnboardingMutation.isLoading ? 'Completing...' : 'Complete Setup'}
          </Button>
        )}

        <Button
          variant="outline"
          onClick={handleSkipForNow}
          className="w-full"
        >
          Skip for Now
        </Button>
      </div>
    </div>
  )

  const StepByStepView = () => (
    <div className="p-8">
      {/* Step Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setIsStepByStep(false)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            Step {currentStep + 1} of {steps.length}
          </span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex space-x-2">
          {steps.map((_, index) => (
            <div
              key={index}
              className={cn(
                "h-2 rounded-full flex-1 transition-all duration-200",
                index <= currentStep
                  ? "bg-accent-600"
                  : "bg-neutral-200 dark:bg-neutral-700"
              )}
            />
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="min-h-[400px]">
        {currentStepData?.content}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-neutral-700">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={currentStep === 0}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>

        <Button
          onClick={handleNext}
          disabled={currentStep === steps.length - 1}
        >
          Next
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#1a1625] rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {isStepByStep ? <StepByStepView /> : <WelcomeView />}
      </div>
    </div>
  )
}

export default OnboardingModal